import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from "uuid";
import $ from 'jquery';
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { SyncLoader } from "react-spinners";

const LanguageIso2Enum = Object.freeze({
    EN: "en",
    DE: "de",
});

const ThemeEnum = Object.freeze({
    LIGHT: "light",
    DARK: "dark",
});

const DisplayTypeEnum = Object.freeze({
    EMBEDEED: 1,
    POPUP: 2,
});

const MessageTypeEnum = Object.freeze({
    BOT: "bot",
    USER: "user",
});

const validateChatbotInitParams = (apiUrl, sectionName, languageIso2, displayType, inputPlaceholder, initialChatMessage, hiMessage, hiMessage2, checkConsent, privacyPageUrl) => {
    if (!apiUrl) {
        throw ("'apiUrl' is required to initialize Chatbot!");
    }
    if (!sectionName) {
        throw ("'sectionName' is required to initialize Chatbot!");
    }
    if (!languageIso2) {
        throw ("'languageIso2' is required to initialize Chatbot!");
    }
    if (!displayType) {
        throw ("'displayType' is required to initialize Chatbot!");
    }
    if (!inputPlaceholder) {
        throw ("'inputPlaceholder' is required to initialize Chatbot!");
    }
    if (!initialChatMessage) {
        throw ("'initialChatMessage' is required to initialize Chatbot!");
    }
    if (!hiMessage) {
        throw ("'hiMessage' is required to initialize Chatbot!");
    }
    if (!hiMessage2) {
        throw ("'hiMessage2' is required to initialize Chatbot!");
    }
    if (checkConsent && !privacyPageUrl) {
        throw ("'privacyPageUrl' is required to initialize Chatbot!");
    }
};

const useCookieConsent = () => {
    const [consentGiven, setConsentGiven] = useState(false);

    useEffect(() => {
        const checkConsent = () => {
            if (window.CookieConsent && window.CookieConsent) {
                const consented = window.Cookiebot.consented;
                setConsentGiven(consented);
            }
        };

        const checkCookieConsentAvailability = () => {
            if (window.CookieConsent) {
                checkConsent();
            } else {
                setTimeout(checkCookieConsentAvailability, 100);
            }
        };

        checkCookieConsentAvailability();

        const handleAcceptEvent = () => {
            setConsentGiven(true);
        };

        const handleDeclineEvent = () => {
            setConsentGiven(false);
        };

        window.addEventListener("CookiebotOnAccept", handleAcceptEvent);
        window.addEventListener("CookiebotOnDecline", handleDeclineEvent);
        window.addEventListener("CookiebotOnConsentReady", checkConsent);

        return () => {
            window.removeEventListener("CookiebotOnAccept", handleAcceptEvent);
            window.removeEventListener("CookiebotOnDecline", handleDeclineEvent);
            window.removeEventListener("CookiebotOnConsentReady", checkConsent);
        };
    }, []);

    return consentGiven;
};

const useLocalStorageListener = (key) => {
    const getStoredValue = () => localStorage.getItem(key) || "default";

    const [value, setValue] = useState(getStoredValue);

    useEffect(() => {
        const checkLocalStorage = () => {
            const currentValue = getStoredValue();
            if (currentValue !== value) {
                setValue(currentValue);
            }
        };

        const interval = setInterval(checkLocalStorage, 100);

        return () => {
            clearInterval(interval);
        };
    }, [key, value]);

    return value;
};

const useMobileDetect = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return isMobile;
};

const PrivacyPolicyComponent = ({ displayType, privacyPageUrl, languageIso2, hiMessage }) => {
    const theme = useLocalStorageListener("theme");
    const isMobile = useMobileDetect();

    const handleButtonClick = () => {
        localStorage.setItem("privacy_policy_consent", "accepted");
    }

    const consentMessage = languageIso2 === LanguageIso2Enum.DE
        ? `Ich kann Ihnen erst nach dem Akzeptieren der <a href="${privacyPageUrl}" style="color: var(--text-email-contact); text-decoration: underline;" target="_blank">Datenschutzerklärung</a> helfen.`
        : `I can assist you only after accepting the <a href="${privacyPageUrl}" style="color: var(--text-email-contact); text-decoration: underline;" target="_blank">Privacy Policy</a>.`;

    const acceptCaption = languageIso2 === LanguageIso2Enum.DE
        ? "Akzeptieren"
        : "Accept"

    return (
        <div
            className="relative min-h-screen"
            style={{
                height: "100%",
                width: "100%",
                padding: displayType === DisplayTypeEnum.EMBEDEED ? (isMobile ? "20px" : "60px") : "15px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexFlow: "column"
            }}>

            <style>
                {` *{
                     box-sizing: border-box;
                    }
                `}
            </style>

            <span style={{
                textAlign: "center",
                display: "block",
                fontSize: isMobile ? "40px" : "60px",
                fontWeight: "700",
                lineHeight: isMobile ? "44px" : "68px",
                color: displayType === DisplayTypeEnum.POPUP ? (theme === ThemeEnum.DARK ? "#ffffff" : "#091b61") : "#ffffff"
            }}>{hiMessage}</span>

            <span
                style={{
                    textAlign: "center",
                    display: "block",
                    fontSize: isMobile ? "20px" : "24px",
                    lineHeight: isMobile ? "25px" : "30px",
                    color: displayType === DisplayTypeEnum.POPUP ? (theme === ThemeEnum.DARK ? "#ffffff" : "#091b61") : "#ffffff"
                }}
                dangerouslySetInnerHTML={{ __html: consentMessage }}
            />

            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: displayType === DisplayTypeEnum.EMBEDEED ? (isMobile ? "20px" : "30px") : "15px"
            }}>
                <button
                    onClick={handleButtonClick}
                    style={{
                        marginRight: isMobile ? "0px" : "10px",
                        marginBottom: "10px",
                        padding: "11px 30px",
                        fontSize: "16px",
                        lineHeight: "20px",
                        backgroundColor: "#4165ff",
                        border: "#4165ff",
                        color: "#FFFFFF",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontWeight: "600",
                    }}
                >
                    {acceptCaption}
                </button>
            </div>
        </div>);
}

// const TypingEffect = ({ message, typingSpeed = 50, style, onCompleteWord, onTypingStart, onTypingComplete }) => {
//     const [displayedText, setDisplayedText] = useState('');
//     const [index, setIndex] = useState(0);
//     const [typingStarted, setTypingStarted] = useState(false);

//     const getPlainTextFromJSX = (jsx) => {
//         if (typeof jsx === 'string') return jsx;
//         if (jsx.props && jsx.props.dangerouslySetInnerHTML) {
//             return jsx.props.dangerouslySetInnerHTML.__html;
//         }
//         return '';
//     };

//     const plainText = getPlainTextFromJSX(message);

//     useEffect(() => {
//         setDisplayedText('');
//         setIndex(0);
//         setTypingStarted(false);
//     }, [plainText]);

//     useEffect(() => {
//         if (index < plainText.length) {

//             if (!typingStarted) {
//                 setTypingStarted(true);
//                 if (onTypingStart) {
//                     onTypingStart();
//                 }
//             }

//             const timeout = setTimeout(() => {
//                 setDisplayedText((prev) => prev + plainText[index]);
//                 setIndex((prev) => prev + 1);
//             }, typingSpeed);
//             return () => clearTimeout(timeout);
//         }
//         else if (index === plainText.length && typingStarted) {
//             if (onTypingComplete) {
//                 onTypingComplete();
//             }

//             setTypingStarted(false);
//         }
//     }, [index, plainText, typingSpeed, onTypingComplete]);

//     onCompleteWord();

//     return (
//         <div>
//             <div style={style}>
//                 <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
//                     {displayedText}
//                 </ReactMarkdown>
//             </div>
//         </div>
//     );
// };

const TypingEffect = ({
    message,
    typingSpeed = 50,
    style,
    onCompleteWord,
    onTypingStart,
    onTypingComplete
}) => {
    const [displayedText, setDisplayedText] = useState('');
    const [index, setIndex] = useState(0);
    const [typingStarted, setTypingStarted] = useState(false);

    const getPlainTextFromJSX = (jsx) => {
        if (typeof jsx === 'string') return jsx;
        if (jsx.props && jsx.props.dangerouslySetInnerHTML) {
            return jsx.props.dangerouslySetInnerHTML.__html;
        }
        return '';
    };

    const plainText = getPlainTextFromJSX(message);

    useEffect(() => {
        setDisplayedText('');
        setIndex(0);
        setTypingStarted(false);
    }, [plainText]);

    useEffect(() => {
        if (index < plainText.length) {
            if (!typingStarted) {
                setTypingStarted(true);
                onTypingStart?.();
            }

            const timeout = setTimeout(() => {
                setDisplayedText(prev => prev + plainText[index]);
                setIndex(prev => prev + 1);
            }, typingSpeed);

            return () => clearTimeout(timeout);
        }

        if (index === plainText.length && typingStarted) {
            onTypingComplete?.();
            setTypingStarted(false);
        }
    }, [index, plainText, typingSpeed, typingStarted, onTypingStart, onTypingComplete]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                setDisplayedText(plainText);
                setIndex(plainText.length);
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () =>
            document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, [plainText]);

    onCompleteWord?.();

    return (
        <div>
            <div style={style}>
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                >
                    {displayedText}
                </ReactMarkdown>
            </div>
        </div>
    );
};


const LoadingSpinnerComponent = ({ displayType }) => {
    const theme = useLocalStorageListener("theme");

    return (
        <SyncLoader
            color={displayType === DisplayTypeEnum.POPUP ? (theme === ThemeEnum.DARK ? "#ffffff" : "#091b61") : "#ffffff"}
            size={5}
            margin={3} />)
};

const LikeUnlikeComponent = ({ apiUrl, messageId, token, userId, sessionId, rateMessage, initialLiked = false, initialUnliked = false, displayType }) => {
    const [liked, setLiked] = useState(initialLiked);
    const [unliked, setUnliked] = useState(initialUnliked);

    const theme = useLocalStorageListener("theme");

    const rateResponse = async (messageId, rating) => {
        await axios.post(
            apiUrl + "/rate_response",
            {
                user_id: userId,
                session_id: sessionId,
                message_id: messageId,
                rating: rating
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
            }
        );
    }

    const handleLike = async (messageId) => {
        if(liked){
            return;
        }
        await rateResponse(messageId, 1);
        setLiked(true);
        setUnliked(false);
    };

    const handleUnlike = async (messageId) => {
        if(unliked){
            return;
        }
        await rateResponse(messageId, -1);
        setUnliked(true);
        setLiked(false);
    };

    return (
        <div className="max-w-sm mx-auto p-4 shadow-md rounded-2xl">
            <div style={{
                display: "flex",
                padding: "10px 10px 10px 0px",
                alignItems: "center"
            }}>
                <div style={{
                    fontSize: "12px",
                    marginRight: "10px"

                }}>{rateMessage}</div>
                <div style={{
                    width: "40px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
                >
                    <div
                        style={{
                            cursor: "pointer",
                            width: "16px",
                            height: "16px",
                        }}
                        onClick={() => handleLike(messageId)}
                        className={`flex items-center space-x-2 ${liked ? 'text-blue-600' : 'text-gray-600'}`}
                    >
                        {liked ? (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path style={{ fill: displayType === DisplayTypeEnum.POPUP ? (theme === ThemeEnum.DARK ? "#ffffff" : "#091b61") : "#ffffff" }} d="M0.332031 12.334H1.66536C2.03203 12.334 2.33203 12.034 2.33203 11.6673V5.66732C2.33203 5.30065 2.03203 5.00065 1.66536 5.00065H0.332031V12.334ZM13.552 7.58732C13.6254 7.42065 13.6654 7.24065 13.6654 7.05398V6.33398C13.6654 5.60065 13.0654 5.00065 12.332 5.00065H8.66537L9.2787 1.90065C9.31203 1.75398 9.29203 1.59398 9.22536 1.46065C9.07203 1.16065 8.8787 0.887318 8.6387 0.647318L8.33203 0.333984L4.0587 4.60732C3.80536 4.86065 3.66536 5.20065 3.66536 5.55398V10.7807C3.66536 11.634 4.36537 12.334 5.22536 12.334H10.632C11.0987 12.334 11.5387 12.0873 11.7787 11.6873L13.552 7.58732Z" fill="white" fillOpacity="0.75" />
                            </svg>
                        ) : (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path style={{ fill: displayType === DisplayTypeEnum.POPUP ? (theme === ThemeEnum.DARK ? "#ffffff" : "#091b61") : "#ffffff" }} d="M9.3325 3.79L8.905 5.9575C8.815 6.4 8.935 6.8575 9.22 7.2025C9.505 7.5475 9.925 7.75 10.375 7.75H14.5V8.56L12.5725 13H6.505C6.37 13 6.25 12.88 6.25 12.745V6.865L9.3325 3.79ZM10.345 1.3525C10.1551 1.15844 9.84324 1.15676 9.65123 1.34877L5.1925 5.8075C4.9075 6.0925 4.75 6.475 4.75 6.8725V12.745C4.75 13.7125 5.5375 14.5 6.505 14.5H12.58C13.1125 14.5 13.6 14.2225 13.87 13.7725L15.8725 9.16C15.955 8.9725 16 8.77 16 8.56V7.75C16 6.925 15.325 6.25 14.5 6.25H10.375L11.065 2.7625C11.1025 2.5975 11.08 2.4175 11.005 2.2675C10.8325 1.93 10.615 1.6225 10.345 1.3525ZM2.5 6.25H2C1.44772 6.25 1 6.69772 1 7.25V13.5C1 14.0523 1.44772 14.5 2 14.5H2.5C2.9125 14.5 3.25 14.1625 3.25 13.75V7C3.25 6.5875 2.9125 6.25 2.5 6.25Z" fill="white" fillOpacity="0.75" />
                            </svg>
                        )}
                    </div>
                    <div
                        style={{
                            cursor: "pointer",
                            width: "16px",
                            height: "16px"
                        }}
                        onClick={() => handleUnlike(messageId)}
                        className={`flex items-center space-x-2 ${unliked ? 'text-red-600' : 'text-gray-600'}`}
                    >
                        {unliked ? (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path style={{ fill: displayType === DisplayTypeEnum.POPUP ? (theme === ThemeEnum.DARK ? "#ffffff" : "#091b61") : "#ffffff" }} d="M6.155 12.1475C6.34493 12.3416 6.65676 12.3432 6.84877 12.1512L11.3075 7.6925C11.5925 7.4075 11.75 7.025 11.75 6.6275V0.755C11.75 -0.2125 10.9625 -1 9.995 -1H3.92C3.3875 -1 2.9 -0.7225 2.63 -0.2725L0.6275 4.34C0.545 4.5275 0.5 4.73 0.5 4.94V5.75C0.5 6.575 1.175 7.25 2 7.25H6.125L5.435 10.7375C5.3975 10.9025 5.42 11.0825 5.495 11.2325C5.6675 11.57 5.885 11.8775 6.155 12.1475ZM14 7.25H14.5C15.0523 7.25 15.5 6.80229 15.5 6.25V0C15.5 -0.552285 15.0523 -1 14.5 -1H14C13.5875 -1 13.25 -0.6625 13.25 -0.25V6.5C13.25 6.9125 13.5875 7.25 14 7.25Z" fill="white" fillOpacity="0.75" />
                            </svg>
                        ) : (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path style={{ fill: displayType === DisplayTypeEnum.POPUP ? (theme === ThemeEnum.DARK ? "#ffffff" : "#091b61") : "#ffffff" }} d="M6.6675 12.71L7.095 10.5425C7.185 10.1 7.065 9.6425 6.78 9.2975C6.495 8.9525 6.075 8.75 5.625 8.75H1.5V7.94L3.4275 3.5H9.495C9.63 3.5 9.75 3.62 9.75 3.755V9.635L6.6675 12.71ZM5.655 15.1475C5.84493 15.3416 6.15676 15.3432 6.34877 15.1512L10.8075 10.6925C11.0925 10.4075 11.25 10.025 11.25 9.6275V3.755C11.25 2.7875 10.4625 2 9.495 2H3.42C2.8875 2 2.4 2.2775 2.13 2.7275L0.1275 7.34C0.0450001 7.5275 0 7.73 0 7.94V8.75C0 9.575 0.675 10.25 1.5 10.25H5.625L4.935 13.7375C4.8975 13.9025 4.92 14.0825 4.995 14.2325C5.1675 14.57 5.385 14.8775 5.655 15.1475ZM13.5 10.25H14C14.5523 10.25 15 9.80229 15 9.25V3C15 2.44772 14.5523 2 14 2H13.5C13.0875 2 12.75 2.3375 12.75 2.75V9.5C12.75 9.9125 13.0875 10.25 13.5 10.25Z" fill="white" fillOpacity="0.75" />
                            </svg>

                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ButtonComponent = ({ button, groupId, handleButtonClick, languageIso2, isMobile }) => {
    const theme = useLocalStorageListener("theme");

    return (
        <button
            id={`button-${groupId}-${button.index}`}
            className={`groupId-${groupId}`}
            onClick={() => handleButtonClick(button, groupId)}
            style={{
                marginRight: isMobile ? "0px" : "10px",
                marginBottom: "10px",
                padding: "7px 20px",
                fontSize: "16px",
                lineHeight: "20px",
                backgroundColor: theme === ThemeEnum.LIGHT ? "rgba(224, 232, 255, 1)" : "rgba(10, 16, 41, 1)",
                border: theme === ThemeEnum.LIGHT ? "1px solid rgba(8, 80, 247, 1)" : "1px solid rgba(131, 154, 255, 1)",
                color: theme === ThemeEnum.LIGHT ? "rgba(8, 80, 247, 1)" : "rgba(131, 154, 255, 1)",
                borderRadius: "50px",
                cursor: "pointer",
                fontWeight: "600",
            }}
        >
            {button.caption[languageIso2]}
        </button>
    );
};

const RegenerateResponseComponent = ({ handleRegenerate, languageIso2, displayType }) => {
    const theme = useLocalStorageListener("theme");

    let errorMessasge = "";
    let buttonMessage = "";
    if (languageIso2 == "de") {
        errorMessasge = "Es ist ein Fehler aufgetreten, bitte klicken Sie auf „Neu generieren“!";
        buttonMessage = "Neu generieren";
    }
    else {
        errorMessasge = "An error occurred, please click on regenerate!";
        buttonMessage = "Regenerate response";
    }

    return (
        <div>
            <p
                style={{
                    color: "red",
                    marginBottom: "0px"
                }}>
                {errorMessasge}
            </p>

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    padding: "10px 10px 10px 0px"
                }}
                onClick={handleRegenerate}
            >
                <svg
                    style={{
                        marginRight: "10px",
                        cursor: "pointer",
                    }}
                    width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"
                >
                    <path style={{ fill: displayType === DisplayTypeEnum.POPUP ? (theme === ThemeEnum.DARK ? "#ffffff" : "#091b61") : "#ffffff" }} d="M12.9809 8.02587C12.7996 7.96935 12.6033 7.98681 12.4348 8.07443C12.2663 8.16205 12.1393 8.31274 12.0814 8.49363C11.7414 9.53312 11.0786 10.437 10.1895 11.0738C9.30027 11.7106 8.23111 12.0471 7.1375 12.0342C5.78484 12.0496 4.48128 11.5279 3.51268 10.5836C2.54408 9.6393 1.98949 8.3494 1.97052 6.99679C1.98949 5.64417 2.54408 4.35428 3.51268 3.40996C4.48128 2.46563 5.78484 1.94397 7.1375 1.95934C8.35914 1.95639 9.54318 2.38163 10.4838 3.16113L8.9222 2.90206C8.82861 2.88666 8.73289 2.88992 8.64057 2.91164C8.54824 2.93336 8.46111 2.97311 8.3842 3.02862C8.30729 3.08413 8.24212 3.1543 8.19242 3.23508C8.14272 3.31587 8.10948 3.40568 8.09462 3.49936C8.07922 3.59295 8.08247 3.68866 8.10419 3.78099C8.12591 3.87332 8.16567 3.96044 8.22118 4.03735C8.27668 4.11426 8.34685 4.17944 8.42764 4.22914C8.50842 4.27884 8.59824 4.31208 8.69191 4.32694L11.7432 4.83069H11.8655C11.9489 4.83059 12.0317 4.81598 12.1102 4.78751C12.1365 4.7775 12.1609 4.76288 12.1821 4.74433C12.2337 4.72514 12.2822 4.69847 12.3261 4.66517L12.3908 4.58601C12.3908 4.55003 12.4556 4.52124 12.4844 4.47806C12.5132 4.43489 12.4844 4.4061 12.5204 4.37732C12.5403 4.3354 12.5571 4.29211 12.5707 4.24778L13.1105 1.36924C13.1284 1.27474 13.1276 1.17762 13.108 1.08344C13.0884 0.989258 13.0505 0.899855 12.9964 0.820334C12.9423 0.740813 12.873 0.672732 12.7926 0.619978C12.7121 0.567224 12.6221 0.53083 12.5276 0.512874C12.4331 0.494919 12.3359 0.495753 12.2418 0.515329C12.1476 0.534905 12.0582 0.57284 11.9787 0.626967C11.8181 0.736283 11.7075 0.90492 11.6712 1.09578L11.4769 2.13925C10.2722 1.09523 8.73162 0.520381 7.1375 0.520071C5.40313 0.504719 3.73351 1.17803 2.49499 2.39226C1.25648 3.60649 0.550242 5.26245 0.53125 6.99679C0.550242 8.73112 1.25648 10.3871 2.49499 11.6013C3.73351 12.8155 5.40313 13.4889 7.1375 13.4735C8.54305 13.4951 9.9184 13.0648 11.061 12.2461C12.2037 11.4273 13.0533 10.2633 13.4847 8.92541C13.512 8.83327 13.5205 8.73661 13.5099 8.64111C13.4992 8.54561 13.4695 8.45321 13.4226 8.36937C13.3756 8.28552 13.3124 8.21193 13.2365 8.15294C13.1607 8.09394 13.0738 8.05074 12.9809 8.02587Z" fill="white" fillOpacity="0.75" />
                </svg>

                <label
                    style={{
                        fontSize: "12px",
                        fill: displayType === DisplayTypeEnum.POPUP ? (theme === ThemeEnum.DARK ? "#ffffff" : "#091b61") : "#ffffff",
                        cursor: "pointer",
                        marginBottom: "0px"
                    }}>
                    {buttonMessage}
                </label>
            </div>
        </div>
    )
};

const ChatApp = ({ apiUrl ,sectionName, languageIso2, displayType, inputPlaceholder, rateMessage, initialChatMessage, hiMessage, hiMessage2, checkConsent = false, privacyPageUrl }) => {
    // const apiUrl = "https://dev-temi-function.azurewebsites.net";   // DEV
    // const apiUrl = "https://prod-temi-function.azurewebsites.net";  // PRODUCTION
    // const apiUrl = "https://chat-api.temera.ae";  // NEW PRODUCTION
    const user = "webflow";
    const password = "tmz_dev_2031";

    validateChatbotInitParams(apiUrl, sectionName, languageIso2, displayType, inputPlaceholder, initialChatMessage, hiMessage, hiMessage2, checkConsent, privacyPageUrl);

    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [autoScroll, setAutoScroll] = useState(true);
    const consentGiven = useLocalStorageListener("privacy_policy_consent");

    const chatContainerRef = useRef(null);
    const textareaRef = useRef(null);
    const theme = useLocalStorageListener("theme");
    const websiteDomain = window.location.origin;
    const instanceId = uuidv4();
    const isMobile = useMobileDetect();

    useEffect(() => {
        if (checkConsent && consentGiven !== "accepted") {
            setMessages([]);
            return;
        };

        setMessages([]);
        sendMessage(initialChatMessage);
    }, [consentGiven]);

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const removeLastMessage = () => {
        setMessages((prevMessages) => prevMessages.slice(0, -1));
    };

    // Storage SECTION
    const setToken = (access_token) => {
        sessionStorage.setItem("access_token", access_token);
    };
    const setUserId = (userId) => {
        sessionStorage.setItem(`user_id_${sectionName}`, userId);
    };
    const setSessionId = (sessionId) => {
        sessionStorage.setItem(`session_id_${sectionName}`, sessionId);
    };

    const getToken = () => {
        return sessionStorage.getItem("access_token") ?? "";
    };
    const getUserId = () => {
        return sessionStorage.getItem(`user_id_${sectionName}`) ?? "";
    };
    const getSessionId = () => {
        return sessionStorage.getItem(`session_id_${sectionName}`) ?? "";
    };
    const getSelectedLanguage = () => {
        return localStorage.getItem("selectedLang");
    };
    // Storage SECTION

    // Auto scroll SECTION
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTo({
                top: chatContainerRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    }, [messages]);

    useEffect(() => {
        const handleScroll = () => {
            if (chatContainerRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
                const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;
                setAutoScroll(isAtBottom);
            }
        };

        if (chatContainerRef.current) {
            chatContainerRef.current.addEventListener("scroll", handleScroll);

            const observer = new MutationObserver(() => {
                if (autoScroll) {
                    requestAnimationFrame(() => {
                        chatContainerRef.current.scrollTo({
                            top: chatContainerRef.current.scrollHeight,
                            behavior: "smooth",
                        });
                    });
                }
            });

            observer.observe(chatContainerRef.current, { childList: true, subtree: true });

            return () => {
                chatContainerRef.current.removeEventListener("scroll", handleScroll);
                observer.disconnect();
            };
        }
    }, [autoScroll]);

    const onCompleteWord = () => {
        if (autoScroll && chatContainerRef.current) {
            chatContainerRef.current.scrollTo({
                top: chatContainerRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    };
    const onTypingComplete = () => {
        setTimeout(function () {
            setIsTyping(false);
        }, 500)
    };
    const onTypingStart = () => {
        setIsTyping(true);
    };
    // Auto scroll SECTION

    // Chat UTILS
    const parseChatPayload = (payload) => {
        const parseUrls = (payload) => {
            let make_urls = payload
                .split("\n")
                .filter((line) => line.includes("{"))
                .map((line) => JSON.parse(line.replace("data: ", "")))
                .filter(
                    (item) =>
                        item.actions !== undefined &&
                        item.actions.length > 0 &&
                        item.actions[0]?.make_urls?.length > 0
                )
                .map((item) => item.actions[0].make_urls);

            return make_urls[0] ?? [];
        }

        const parseButtons = (payload) => {
            let make_buttons = payload
                .split("\n")
                .filter((line) => line.includes("{"))
                .map((line) => JSON.parse(line.replace("data: ", "")))
                .filter(
                    (item) =>
                        item.actions !== undefined &&
                        item.actions.length > 0 &&
                        item.actions[0]?.make_buttons?.length > 0
                )
                .map((item) => item.actions[0].make_buttons);
            return make_buttons[0] ?? [];
        }

        const parseReplyText = (payload) => {
            const replyText = payload
                .split("\n")
                .filter((line) => line.includes("{"))
                .map((line) => JSON.parse(line.replace("data: ", "")))
                .filter((item) => item.reply !== undefined)
                .map((item) => item.reply)
                .join("");

            return replyText;
        }

        return {
            make_urls: parseUrls(payload),
            make_buttons: parseButtons(payload),
            replyText: parseReplyText(payload)
        }
    };
    const drawChatPayload = (chatPayloadParsed, messageId) => {

        const replaceUrls = (make_urls, replyText) => {
            let formattedReply = replyText;

            let langPrefixUrl;
            if (getSelectedLanguage() === "de") {
                langPrefixUrl = "/de";
            } else {
                langPrefixUrl = "/"
            }

            if (make_urls.length > 0) {
                make_urls.forEach(({ target, internal, display_text }) => {
                    let link;

                    if (internal) {
                        let updatedTarget = target.startsWith("/") ? target.substring(1) : target;
                        link = `<a href="${websiteDomain + langPrefixUrl + "#" + updatedTarget}" style="color: var(--text-email-contact); text-decoration: underline;">${display_text}</a>`;
                    } else {
                        link = `<a href="${target}" target="_blank" style="color: var(--text-email-contact); text-decoration: underline;">${display_text}</a>`;
                    }

                    return formattedReply = formattedReply.replace(display_text, link);
                });
            }

            return formattedReply;
        }

        const handleButtonClick = async (button, groupId) => {
            const { api_route, api_params } = button;

            $(`.groupId-${groupId}`).prop('disabled', true).css({
                cursor: "not-allowed",
                opacity: "50%"
            });
            $(`#button-${groupId}-${button.index}`).css({
                backgroundColor: "#839AFF",
                color: "#1E0530",
                opacity: "100%"
            });

            const userMessage = {
                message: <span dangerouslySetInnerHTML={{ __html: button.chat_message[languageIso2] }} style={{ fontSize: "16px", lineHeight: "18px" }} />,
                type: MessageTypeEnum.USER,
                showTyping: true,
                showRateSection: true
            };
            setMessages((prev) => [...prev, userMessage]);

            api_params.user_id = getUserId();
            api_params.session_id = getSessionId();

            api_params.message = button.chat_message[languageIso2];

            try {
                setIsLoading(true);
                const response = await axios.post(
                    apiUrl + api_route,
                    { ...button.api_params },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${getToken()}`
                        },
                    }
                );

                const data = response.data;

                const reply = data
                    .split("\n")
                    .filter((line) => line.includes("{"))
                    .map((line) => JSON.parse(line.replace("data: ", "")))
                    .filter((item) => item.reply !== undefined)
                    .map((item) => item.reply)
                    .join("");

                let messageId = "";
                if (response.status === 200) {
                    messageId = await assistantMessageSent(reply);
                }

                const botMessage = {
                    messageId: messageId,
                    message: <span dangerouslySetInnerHTML={{ __html: reply }} style={{ fontSize: "16px", lineHeight: "18px" }} />,
                    type: MessageTypeEnum.BOT,
                    showTyping: true,
                    showRateSection: true
                };
                setMessages((prev) => [...prev, botMessage]);
            } catch (error) {
                console.error("Error handling button click:", error);
            } finally {
                setIsLoading(false);
            }
        };

        let replyText = chatPayloadParsed.replyText;

        if (chatPayloadParsed.make_buttons.length > 0) {
            const groupId = uuidv4();
            const buttonElements = chatPayloadParsed.make_buttons.map((button) => (
                <ButtonComponent
                    key={button.index}
                    button={button}
                    groupId={groupId}
                    handleButtonClick={handleButtonClick}
                    languageIso2={languageIso2}
                    isMobile={isMobile}
                />
            ));

            setMessages((prev) => [
                ...prev,
                {
                    message: buttonElements,
                    type: MessageTypeEnum.BOT,
                    showTyping: false,
                    showRateSection: false
                },
            ]);
        }
        else {
            if (chatPayloadParsed.make_urls.length > 0) {
                replyText = replaceUrls(chatPayloadParsed.make_urls, chatPayloadParsed.replyText);
            }

            const botMessage = {
                messageId: messageId,
                message: <span dangerouslySetInnerHTML={{ __html: replyText }} style={{ fontSize: "16px", lineHeight: "18px" }} />,
                type: MessageTypeEnum.BOT,
                showTyping: true,
                showRateSection: true
            };

            if (replyText !== "") {
                setMessages((prev) => [...prev, botMessage]);
            }
        }
    };
    // Chat UTILS

    const registerUserSession = async () => {

        const getSessionInfo = () => {
            const getOperatingSystem = () => {
                const userAgent = navigator.userAgent || navigator.vendor || window.opera;

                if (userAgent.includes("Windows Phone")) {
                    return "Windows Phone";
                }
                if (userAgent.includes("Android")) {
                    return "Android";
                }
                if ((userAgent.includes("iPad") || userAgent.includes("iPhone") || userAgent.includes("iPod")) && !window.MSStream) {
                    return "iOS";
                }
                if (userAgent.includes("Macintosh") || userAgent.includes("MacIntel") || userAgent.includes("MacPPC") || userAgent.includes("Mac68K")) {
                    return "MacOS";
                }
                if (userAgent.includes("Win32") || userAgent.includes("Win64") || userAgent.includes("Windows") || userAgent.includes("WinCE")) {
                    return "Windows";
                }
                if (userAgent.includes("Linux") || userAgent.includes("X11")) {
                    return "Linux";
                }
                if (userAgent.includes("CrOS")) {
                    return "Chrome OS";
                }

                return "Unknown OS";
            };

            const getBrowserName = () => {
                const userAgent = navigator.userAgent;

                if (userAgent.includes("Firefox")) {
                    return "Mozilla Firefox";
                } else if (userAgent.includes("Edg")) {
                    return "Microsoft Edge";
                } else if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) {
                    return "Google Chrome";
                } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
                    return "Safari";
                }

                return "Unknown Browser";
            };

            return {
                browser: getBrowserName(),
                operatingSystem: getOperatingSystem(),
                screenHeight: window.screen.height,
                screenWidth: window.screen.width
            }
        };

        let userId = uuidv4();
        let sessionId = uuidv4();

        const response = await axios.post(
            apiUrl + "/register_user_session",
            {
                user_id: userId,
                session_id: sessionId,
                session_info: getSessionInfo()
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`
                },
            }
        );

        if (response.status === 200) {
            setUserId(userId);
            setSessionId(sessionId);
        }
    };

    const loginUser = async () => {
        try {
            const response = await axios.post(
                apiUrl + "/login",
                {
                    user: user,
                    password: password,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status !== 200) {
                throw new Error(`Error: ${response.status}`);
            }

            setToken(response.data.access_token);

            if (!getUserId() || !getSessionId()) {
                await registerUserSession();
            }

            return response.data.access_token;
        } catch (error) {
            console.error("Login failed:", error.message);
            return null;
        }
    };

    const sendMessage = async (message = "") => {
        if (isLoading || isTyping) {
            return;
        }

        const userMessage = {
            message: <span dangerouslySetInnerHTML={{ __html: input }} style={{ fontSize: "16px", lineHeight: "18px" }} />,
            type: MessageTypeEnum.USER,
            showTyping: true,
            showRateSection: true
        };

        if (input.trim()) {
            setMessages((prev) => [...prev, userMessage]);
        }

        let inputValue = input;
        setInput("");

        if (message) {
            inputValue = message;
        }

        if (inputValue === "") {
            return;
        }

        try {
            setIsLoading(true);

            if (getToken() && (!getUserId() || !getSessionId())) {
                await registerUserSession();
            }

            const response = await axios.post(
                apiUrl + "/chat",
                {
                    message: inputValue,
                    user_id: getUserId(),
                    session_id: getSessionId(),
                    section_name: sectionName
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${getToken()}`
                    },
                }
            );

            let chatPayloadParsed = parseChatPayload(response.data);

            let messageId = "";
            if (response.status === 200) {
                messageId = await assistantMessageSent(chatPayloadParsed.replyText, chatPayloadParsed.make_urls, chatPayloadParsed.make_buttons);
            }

            drawChatPayload(chatPayloadParsed, messageId);

        } catch (error) {
            if (error.response && error.response.status === 401) {
                console.error("Internal Server Error, re-logging in...");

                const loginData = await loginUser();

                if (loginData) {
                    sendMessage(inputValue);
                }
            } else if (error.code === "ERR_NETWORK" || (error.response && (error.response.status === 500 || error.response.status === 403))) {
                // IF ERR_NETWORK || 500 || 403  => DISPLAY REGENERATE RESPONSE
                const userMessage = {
                    type: MessageTypeEnum.BOT,
                    lastInputValue: inputValue,
                    showTyping: false,
                    showRateSection: false,
                    error: true,
                };
                setMessages((prev) => [...prev, userMessage]);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const assistantMessageSent = async (message, urls, buttons) => {
        const response = await axios.post(
            apiUrl + "/assistant_msg_sent",
            {
                message: message,
                user_id: getUserId(),
                session_id: getSessionId(),
                section_name: sectionName,
                buttons: buttons ?? [],
                urls: urls ?? [],
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`
                },
            }
        );

        return response.data.message_id;
    };

    const handleRegenerate = async (lastInputValue) => {
        try {
            setIsLoading(true);

            removeLastMessage();

            const response = await axios.post(
                apiUrl + "/regenerate_response",
                {
                    message: lastInputValue,
                    user_id: getUserId(),
                    session_id: getSessionId(),
                    section_name: sectionName
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${getToken()}`
                    },
                }
            );

            let chatPayloadParsed = parseChatPayload(response.data);

            let messageId = "";
            if (response.status === 200) {
                messageId = await assistantMessageSent(chatPayloadParsed.replyText, chatPayloadParsed.make_urls, chatPayloadParsed.make_buttons);
            }

            drawChatPayload(chatPayloadParsed, messageId);

            setIsLoading(false);
        } catch (error) {
            // IF 409 => CALL CHAT AGAIN
            if (error.response && error.response.status === 409) {
                await sendMessage(initialChatMessage);
            }
        }
    };

    useEffect(() => {
        if (textareaRef.current) {
            const placeholderColor =
                theme === ThemeEnum.LIGHT ? "rgba(9, 27, 97, 1)" : "rgba(255, 255, 255, 0.75)";

            textareaRef.current.style.setProperty("--placeholder-color", placeholderColor);
        }
    }, [theme]);

    return (
        (checkConsent && consentGiven !== "accepted") ?
            (
                <PrivacyPolicyComponent
                    displayType={displayType}
                    privacyPageUrl={privacyPageUrl}
                    languageIso2={languageIso2}
                    hiMessage={hiMessage}
                />
            )
            :
            (<div
                className="relative min-h-screen"
                style={{
                    height: "100%",
                    width: "100%",
                    padding: displayType === DisplayTypeEnum.EMBEDEED ? (isMobile ? "20px" : "60px") : "15px"
                }}>
                <style>
                    {`
                    *{
                     box-sizing: border-box;
                    }

                    div p:first-child{
                        margin-top: 0px!important;
                    }

                    div p:last-child{
                        margin-bottom: 0px!important;
                    }

                    textarea::placeholder {
                        color: var(--placeholder-color, gray);
                    }

                    table, th, td {
                        border: 1px solid;
                        border-collapse: collapse;
                        padding: 5px;
                    }
                `}
                </style>

                {/* Chat Messages */}
                <div
                    ref={chatContainerRef}
                    style={{
                        borderRadius: "10px",
                        height: "-webkit-calc(100% - 148px)",
                        height: "calc(100% - 148px)",
                        overflowY: "scroll",
                        paddingRight: "5px",
                        position: "relative"
                    }}
                >

                    <span style={{
                        display: displayType === DisplayTypeEnum.EMBEDEED ? "block" : "none",
                        fontSize: isMobile ? "40px" : "60px",
                        fontWeight: "700",
                        lineHeight: isMobile ? "44px" : "68px",
                        color: "#FFFFFF"
                    }}>{hiMessage}</span>

                    <span style={{
                        display: displayType === DisplayTypeEnum.EMBEDEED ? "block" : "none",
                        fontSize: isMobile ? "20px" : "28px",
                        lineHeight: isMobile ? "25px" : "30px",
                        color: "#FFFFFF",
                    }}>{hiMessage2}</span>

                    {messages.map((msg, index) => (
                        <div
                            key={index}
                            style={{
                                display: "flex",
                                justifyContent: msg.type === MessageTypeEnum.USER ? "flex-end" : "flex-start",
                                margin: "10px 0"
                            }}
                        >

                            <svg
                                style={{
                                    display: (msg.type !== MessageTypeEnum.USER && msg.message !== "") ? "block" : "none",
                                    marginTop: "15px",
                                    width: "32px",
                                    height: "32px",
                                    viewBox: "0 0 30 30",
                                    fill: "none",
                                    xmlns: "http://www.w3.org/2000/svg"
                                }}>

                                <g filter={`url(#filter0_b_287_2640_${instanceId})`}>
                                    <rect width="30" height="30" rx="6" fill="black" fillOpacity="0.28" />
                                    <rect x="0.5" y="0.5" width="29" height="29" rx="5.5" stroke={`url(#paint0_linear_287_2640_${instanceId})`} stopOpacity="0.5" />
                                    <path d="M12.1622 7.89552C14.4033 5.50329 17.3303 7.04385 18.2628 7.91746L20.5286 10.1035L18.4276 12.3462L13.8348 17.2487L11.0741 20.1957C8.83299 22.5879 6.40765 21.4388 5.47512 20.5652L2.67752 17.9444L8.17203 12.0792C9.70753 10.4402 11.2025 8.91993 12.1622 7.89552Z" fill={`url(#paint1_linear_287_2640_${instanceId})`} />
                                    <path d="M21.7728 21.6414C27.2604 18.3541 25.1589 14.2784 23.4222 12.6514L18.2136 7.77194C17.1448 6.77074 14.4066 5.40957 12.0037 7.97454L6.7474 13.5854C9.1503 11.0205 11.6213 12.1313 12.5565 13.0074L21.7728 21.6414Z" fill={`url(#paint2_linear_287_2640_${instanceId})`} />
                                </g>
                                <defs>
                                    <filter id={`filter0_b_287_2640_${instanceId}`} x="-14" y="-14" width="58" height="58" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                        <feGaussianBlur in="BackgroundImageFix" stdDeviation="7" />
                                        <feComposite in2="SourceAlpha" operator="in" result={`effect1_backgroundBlur_287_2640_${instanceId}`} />
                                        <feBlend mode="normal" in="SourceGraphic" in2={`effect1_backgroundBlur_287_2640_${instanceId}`} result="shape" />
                                    </filter>
                                    <linearGradient id={`paint0_linear_287_2640_${instanceId}`} x1="28.7392" y1="-2.98373e-07" x2="5.16365" y2="33.0344" gradientUnits="userSpaceOnUse">
                                        <stop stopColor="#79B2FA" />
                                        <stop offset="1" stopColor="#4C218F" />
                                    </linearGradient>
                                    <linearGradient id={`paint1_linear_287_2640_${instanceId}`} x1="8.66549" y1="24.8708" x2="5.1955" y2="12.7444" gradientUnits="userSpaceOnUse">
                                        <stop stopColor="#259DFF" />
                                        <stop offset="1" stopColor="#0C275A" />
                                    </linearGradient>
                                    <linearGradient id={`paint2_linear_287_2640_${instanceId}`} x1="8.49601" y1="10.7085" x2="22.9196" y2="12.1806" gradientUnits="userSpaceOnUse">
                                        <stop stopColor="#0CBDFB" />
                                        <stop offset="1" stopColor="#9F21FA" />
                                    </linearGradient>
                                </defs>
                            </svg>

                            <div
                                style={{
                                    maxWidth: msg.type === MessageTypeEnum.USER ? "70%" : "-webkit-calc(100% - 32px)",
                                    maxWidth: msg.type === MessageTypeEnum.USER ? "70%" : "calc(100% - 32px)",
                                    padding: "16px 20px 16px 20px",
                                    backgroundColor: msg.type === MessageTypeEnum.USER ? (displayType === DisplayTypeEnum.EMBEDEED ? "#0D0425" : "#4165FF") : "",
                                    borderRadius: "21px",
                                    fontSize: msg.type === MessageTypeEnum.USER ? "16px" : "16px",
                                    lineHeight: isMobile ? "20px" : "25px",
                                    color: msg.type === MessageTypeEnum.BOT ? (displayType === DisplayTypeEnum.POPUP ? (theme === ThemeEnum.DARK ? "#ffffff" : "#091b61") : "#ffffff") : "#ffffff"
                                }}
                            >

                                {
                                    (msg.type === MessageTypeEnum.BOT && msg.error) ? (
                                        <RegenerateResponseComponent
                                            handleRegenerate={async () => await handleRegenerate(msg.lastInputValue)}
                                            languageIso2={languageIso2}
                                            displayType={displayType}
                                        />
                                    ) :
                                        (msg.type === MessageTypeEnum.BOT && msg.showTyping) ?
                                            <TypingEffect
                                                message={msg.message.props.dangerouslySetInnerHTML.__html}
                                                typingSpeed={10}
                                                style={msg.message.props.style}
                                                onCompleteWord={onCompleteWord}
                                                onTypingStart={onTypingStart}
                                                onTypingComplete={onTypingComplete} />
                                            : (msg.message)
                                }

                                {
                                    (msg.type === MessageTypeEnum.BOT && msg.showRateSection) &&
                                    <LikeUnlikeComponent
                                        apiUrl={apiUrl}
                                        messageId={msg.messageId}
                                        token={getToken()}
                                        userId={getUserId()}
                                        sessionId={getSessionId()}
                                        rateMessage={rateMessage}
                                        displayType={displayType} />
                                }
                            </div>
                        </div>
                    ))}
                </div>

                {/* Loading */}
                <div style={{
                    height: "20px",
                    marginBottom: "5px"
                }}>
                    {isLoading && <LoadingSpinnerComponent displayType={displayType} />}
                </div>

                {/* Input Field */}
                <div style={{ display: "flex", position: "relative" }}>
                    <textarea
                        ref={textareaRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyPress}
                        style={{
                            flex: 1,
                            height: "128px",
                            padding: "10px",
                            font: "Space Grotesk",
                            fontSize: "16px",
                            lineHeight: "32px",
                            borderRadius: "6px",
                            border: theme === ThemeEnum.LIGHT ? "1px solid rgba(9, 27, 97, 0.1)" : "1px solid rgba(255, 255, 255, 0.2)",
                            background: theme === ThemeEnum.LIGHT ? "rgba(231, 233, 251, 1)" : "rgba(13, 4, 37, 1)",
                            color: theme === ThemeEnum.LIGHT ? "rgba(9, 27, 97, 1)" : "rgba(255, 255, 255, 0.75)",
                            resize: "none",
                            outline: "none",

                        }}
                        placeholder={inputPlaceholder}
                    />
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            sendMessage();
                        }}
                        onTouchStart={(e) => {
                            e.preventDefault();
                            sendMessage();
                        }}
                        style={{
                            height: "32px",
                            width: "32px",
                            backgroundColor: "#4165FF",
                            border: "1px solid #4165FF",
                            borderRadius: "4px",
                            cursor: "pointer",
                            position: "absolute",
                            right: "10px",
                            bottom: "10px"
                        }}
                    >
                        <span>▲</span>
                    </button>
                </div>
            </div>)
    );
};

export default ChatApp;